import { electricitySourceLabel } from 'modules/Common/config/cbamElectricityLabels';
import { loadXlsx } from 'modules/Common/utils/loadXlsx';

// xlsx is loaded on demand so the ~550 kB sheets chunk stays out of the initial
// bundle. This module is reachable from an EAGER surface, so a static import
// here holds the chunk in the entry graph no matter how many routes are lazy -
// which is exactly what it was doing before this change.
//
// Module-scoped binding rather than the local `const XLSX = await loadXlsx()`
// default, because the non-exported `buildReferenceSheet` below references
// `XLSX` too. loadXlsx.js states when that trade is worth making, and the
// invariant it costs: EVERY exported entry point must await ensureXlsx()
// first. There is one today - keep it that way, or add the await.
//
// See docs/features/react-lazy-loading/opportunity-register.md.
let XLSX;
// The shared loader memoises, detects the module shape and never latches a failure.
async function ensureXlsx() {
  XLSX = XLSX || (await loadXlsx());
  return XLSX;
}

const slugify = (s) =>
  String(s || 'catalog')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

/**
 * "Electricity emission factor data source" — the operator's own description of
 * where their factor came from.
 *
 * This mirrors the emissions report PDF, which prints
 * `indirectEmissionFactorSourceValue` verbatim under this exact heading
 * (`SalesOrderEmissionsReportPDF.jsx:628`). The sibling `01`/`02` enum is NOT
 * translated to prose here: nothing in the app has ever labelled those codes, so
 * any wording would be invented — and invented regulatory wording is worse than
 * the code itself. It is used only as a last resort, so a factor carrying the
 * code and no free text still says something rather than nothing.
 */
const efDataSourceCell = (e) =>
  e?.emissionFactorSourceValue || e?.otherSourceIndication || e?.emissionFactorSource || '';

const headerRow = [
  'CN Code',
  'Product ID',
  'Installation',
  'Installation city',
  'Installation country',
  'Period',
  'Direct SEE (tCO2e/t)',
  'Indirect SEE (tCO2e/t)',
  'Total SEE (tCO2e/t)',
  'Benchmark (tCO2e/t)',
  'SEFA (tCO2e/t)',
  'CBAM Cost / t (EUR)',
  // Regulatory indirect-emissions detail (CG-2364) — the arithmetic behind the
  // Indirect SEE column, which the on-screen table does not show. Headings and
  // units copied from the emissions report PDF's own rows
  // (`SalesOrderEmissionsReportPDF.jsx:625-628`), so a supplier's spreadsheet
  // and their PDF name the same field the same way.
  'Electricity Consumed (MWh/t)',
  'Electricity Emission Factor (tCO2e/MWh)',
  'Electricity Source',
  'Electricity Emission Factor Data Source',
];

const rowFor = (p, sefa, cost) => {
  const e = p?.electricity ?? null;
  return [
    p?.cnCode ?? '',
    p?.productName ?? '',
    p?.installationName ?? '',
    p?.installationCity ?? '',
    p?.installationCountry ?? '',
    p?.reportingPeriod ?? '',
    p?.direct ?? '',
    p?.indirect ?? '',
    p?.total ?? '',
    p?.defaultBenchmark ?? '',
    p?.sefa != null ? sefa : '',
    cost,
    // A factor can carry a total indirect figure without the electricity
    // breakdown behind it. Blank, not zero — nought MWh is a claim.
    e?.consumed ?? '',
    e?.emissionFactor ?? '',
    electricitySourceLabel(e?.source) ?? '',
    efDataSourceCell(e),
  ];
};

const COLUMN_WIDTHS = [
  14, // CN Code
  16, // Product ID
  28, // Installation
  14, // Installation city
  16, // Installation country
  10, // Period
  18, // Direct
  20, // Indirect
  18, // Total SEE
  18, // Benchmark
  14, // SEFA
  20, // CBAM Cost
  26, // Electricity consumed
  32, // Electricity emission factor
  32, // Electricity source
  44, // Electricity EF data source
];

// Every quarter the portal publishes is an official published average — nothing
// is derived from the weekly closes, because that is not how the official figure
// is computed. `Basis` says so outright rather than leaving the reader to assume
// it. An unrecognised status prints as itself, so a future contract change shows
// up in the file instead of being silently relabelled "official".
const QUARTER_STATUS_LABELS = {
  confirmed: 'Official EEX quarterly average',
};

/**
 * The second sheet: what the numbers on sheet one were priced against.
 *
 * The CBAM Cost column is the product of the supplier's emissions and a
 * certificate price the READER chose — so a spreadsheet carrying that column
 * without the price is unreadable a week later, and worse, looks authoritative.
 * This sheet is what makes the export self-describing.
 */
const buildReferenceSheet = ({ operator, certificatePrice, quarterPrices, yearTag }) => {
  const rows = [
    ['Reference data'],
    [],
    ['Supplier', operator?.name ?? ''],
    ['Portal data last updated', operator?.lastUpdatedAt ?? ''],
    ['Year filter applied', yearTag || 'All years'],
    ['Exported', new Date().toISOString().slice(0, 10)],
    [],
    ['Certificate price used for the CBAM Cost column (EUR/tCO2e)', certificatePrice ?? ''],
    ['This price is the reader’s own assumption, not a figure published by the supplier.'],
    [],
    ['EEX reference certificate prices'],
    ['Quarter', 'Price (EUR/tCO2e)', 'Basis', 'Year'],
  ];

  const quarters = Array.isArray(quarterPrices) ? quarterPrices : [];
  if (quarters.length === 0) {
    rows.push(['No reference prices available']);
  } else {
    quarters.forEach((q) => {
      rows.push([
        q?.quarter ?? '',
        q?.averagePrice ?? '',
        QUARTER_STATUS_LABELS[q?.status] ?? q?.status ?? '',
        q?.year ?? '',
      ]);
    });
  }

  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws['!cols'] = [{ wch: 58 }, { wch: 20 }, { wch: 42 }, { wch: 10 }];
  return ws;
};

const triggerDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/**
 * Export the filtered catalog as a real .xlsx file using SheetJS.
 *
 * Two sheets: `Catalog` (one row per product, including the regulatory
 * electricity breakdown) and `Reference data` (the certificate price the cost
 * column was computed with, and the EEX quarter averages).
 *
 * @param {object[]} products         - filtered list of catalog products
 * @param {function} computeRow       - (product) => { sefa, cost } per row
 * @param {object}   operator         - { name, lastUpdatedAt, ... }
 * @param {string}   yearTag          - active year filter, used in the filename
 * @param {number}   [certificatePrice] - the price behind the CBAM Cost column
 * @param {object[]} [quarterPrices]  - [{ quarter, status, averagePrice, year }]
 */
export const exportCatalogXlsx = async ({
  products,
  computeRow,
  operator,
  yearTag,
  certificatePrice,
  quarterPrices,
}) => {
  await ensureXlsx();

  const dataRows = (products || []).map((p) => {
    const { sefa, cost } = computeRow(p);
    return rowFor(p, sefa, cost);
  });
  const aoa = [headerRow, ...dataRows];

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  // Column widths tuned so the headers and values aren't truncated.
  ws['!cols'] = COLUMN_WIDTHS.map((wch) => ({ wch }));

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Catalog');
  XLSX.utils.book_append_sheet(
    wb,
    buildReferenceSheet({ operator, certificatePrice, quarterPrices, yearTag }),
    'Reference data',
  );

  const buffer = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const filename = `${slugify(operator?.name)}-catalog-${yearTag || 'all'}.xlsx`;
  triggerDownload(blob, filename);
};
